import { Injectable, inject } from '@angular/core';
import { DataOfflineService } from './data-offline.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, tap, catchError } from 'rxjs';
import { BehaviorSubject, firstValueFrom, forkJoin, of } from 'rxjs';
import { filter } from 'rxjs/operators';

import { LayoutService } from './layout.service';
import { applySystemZoom } from '../_util/system-zoom.util';
import { DeviceManagementApiService, DeviceDoc } from './device-management.api.service';
import { DeviceService } from './device.service';
import { updatePreset } from '@primeuix/themes';
import Aura from '@primeuix/themes/aura';
import Lara from '@primeuix/themes/lara';

const presets = {
  Aura,
  Lara,
} as const;

export interface ApiError {
  code: number;
  message: string;
}

export interface ApiResponse<T> {
  status: 'success' | 'error';
  data?: T;
  error?: ApiError;
}

export interface UserSettingsDoc {
  _id?: string;
  _rev?: string;
  displayName?: string;
  email?: string;
  favoriteNoteIds?: string[];
  autosaveEnabled?: boolean;
  showFavorites?: boolean;
  showRecent?: boolean;
  recentDays?: number;
  theme?: string;
  homeView?: string;
  systemZoomFactor?: number;
  editorZoomFactor?: number;
  primaryColor?: string;
  darkMode?: boolean;
  deviceLabel?: string;
  syncMethod?: string;
  savedSearches?: any[]; // Array of SavedSearch objects, persisted to CouchDB and IndexedDB
  [k: string]: any; // index signature to allow additional CouchDB automatic properties
  last_modified?: string;
}

type DisplaySettingsKeys =
  | 'theme'
  | 'homeView'
  | 'systemZoomFactor'
  | 'editorZoomFactor'
  | 'primaryColor'
  | 'darkMode';
type DisplaySettings = Pick<UserSettingsDoc, DisplaySettingsKeys>;

@Injectable({ providedIn: 'root' })
export class UserSettingsService {
  private dataLoadedSubject = new BehaviorSubject<boolean>(false);
  dataLoaded$ = this.dataLoadedSubject.asObservable();

  private userSettingsSubject = new BehaviorSubject<UserSettingsDoc | null>(null);
  userSettings$ = this.userSettingsSubject.asObservable();

  private systemZoomFactorSubject = new BehaviorSubject<number>(150);
  systemZoomFactor$ = this.systemZoomFactorSubject.asObservable();

  private editorZoomFactorSubject = new BehaviorSubject<number>(150);
  editorZoomFactor$ = this.editorZoomFactorSubject.asObservable();

  userSettings: UserSettingsDoc | null = null;
  private accountSettings: UserSettingsDoc | null = null;
  private deviceDisplaySettings: DisplaySettings = {};

  layoutService: LayoutService = inject(LayoutService);

  constructor(
    private http: HttpClient,
    private dataOfflineService: DataOfflineService,
    private deviceApi: DeviceManagementApiService,
    private deviceService: DeviceService,
  ) {}

  // ✅ Called once from AppComponent
  async initialize(): Promise<void> {
    try {
      console.log('[UserSettingsService] Initializing...');

      // 1️⃣ Local theme fallback
      const localColor = localStorage.getItem('primaryColor');
      const localTheme = localStorage.getItem('selectedTheme');
      const localDarkMode = localStorage.getItem('darkMode');
      if (localColor) {
        const colorName = this.resolvePrimaryColorName(localColor);
        if (colorName) {
          this.layoutService.appState.update((s: any) => 
            ({ ...s, primary: colorName }));
        }
        this.applyPrimaryColorValue(localColor);
      }
      if (localTheme) {
        document.documentElement.dataset['theme'] = localTheme;
      }
      if (localDarkMode === 'true' || localDarkMode === 'false') {
        this.layoutService.appState.update((state: any) => ({
          ...state,
          darkMode: localDarkMode === 'true',
        }));
      }

      // 2️⃣ Load from server
      const deviceId = this.deviceService.getOrCreateDeviceId();
      const [userResp, deviceResp] = await firstValueFrom(
        forkJoin([
          this.get().pipe(catchError(() => of({ status: 'error' } as 
                ApiResponse<UserSettingsDoc>))),
          this.deviceApi.get(deviceId).pipe(catchError(() => of({ status: 'error' } as 
                any))),
        ]),
      ); 

      const userSettingsRaw = userResp?.status === 'success' && userResp.data ?
        userResp.data : {};
      userSettingsRaw.favoriteNoteIds = this.normalizeFavoriteNoteIds(
        userSettingsRaw.favoriteNoteIds,
      );
      const userSettings = this.stripDisplaySettings(userSettingsRaw);
      const deviceSettings =
        deviceResp?.status === 'success' && deviceResp.data ?
            this.extractDeviceDisplaySettings(deviceResp.data) : {};
      const mergedDisplay = this.mergeDisplaySettings(userSettingsRaw, deviceSettings);
      const settings = { ...userSettings, ...mergedDisplay };

      this.accountSettings = userSettings;
      this.deviceDisplaySettings = mergedDisplay;
      this.userSettings = settings;
      this.userSettingsSubject.next(settings);

      // 3️⃣ Apply them immediately
      this.applySettings(settings);

      // 4️⃣ Watch for subsequent changes and re-apply (important for zoom consistency)
      this.userSettings$
        .pipe(
          filter((s: UserSettingsDoc | null): s is UserSettingsDoc => !!s),
            ).subscribe((updated: UserSettingsDoc) => this.applySettings(updated)); 

      this.dataLoadedSubject.next(true);
      console.log('[UserSettingsService] User settings loaded:', settings);
    } catch (err) {
      console.error('[UserSettingsService] Initialization error', err);
      this.userSettings = null;
      this.dataLoadedSubject.next(true);
    }
  }

  // ✅ Unified zoom / color / theme application
  private applySettings(settings: UserSettingsDoc): void {
    // Primary color
    if (settings.primaryColor) {
      const colorName = this.resolvePrimaryColorName(settings.primaryColor);
      if (colorName) {
        this.layoutService.appState.update((s: any) => ({ ...s, primary: colorName }));
        this.applyPrimePresetForPrimary(colorName);
      }
      this.applyPrimaryColorValue(settings.primaryColor);
    }

    if (settings.theme) {
      this.applyTheme(settings.theme);
    }

    if (typeof settings.darkMode === 'boolean') {
      this.layoutService.appState.update((s: any) => ({ ...s, darkMode: settings.darkMode }));
      localStorage.setItem('darkMode', settings.darkMode ? 'true' : 'false');
    }

    // Editor zoom
    const editorZoomFactor = settings.editorZoomFactor ?? 100;
    console.log('[UserSettings] applySettings: setting --fontScaleFactor to', editorZoomFactor + '%', 'from factor', settings.editorZoomFactor);
    document.documentElement.style.setProperty('--fontScaleFactor', `${editorZoomFactor}%`);
    document.documentElement.style.setProperty('--fontScaleRatio', `${editorZoomFactor / 100}`);
    this.editorZoomFactorSubject.next(editorZoomFactor);

    // System zoom
    const systemFactor = settings.systemZoomFactor ?? 100;
    applySystemZoom(systemFactor);
    this.systemZoomFactorSubject.next(systemFactor);
  }

  // Attach Authorization header as a fallback in case the global interceptor is inactive
  private authOptions() {
    const token = localStorage.getItem('access_token');
    if (token) {
      const headers = new HttpHeaders({ Authorization: `Bearer ${token}` });
      return { headers };
    }
    return {};
  }

  //##################################################################################
  //#### Calls to Flask REST API ENDPOINTS ####
  //##################################################################################

  private base = '/api/user-settings';
  private favoritesEndpoint = '/api/user-settings/favorites';

  // loads user-settings record for current user from CouchDB
  get(): Observable<ApiResponse<UserSettingsDoc>> {
    return this.http.get<ApiResponse<UserSettingsDoc>>(this.base, this.authOptions());
  }

  //   getUserSettings(): Observable<UserSettingsDoc | undefined> {
  //     return this.http.get<{ status: string; data?: UserSettingsDoc }>(this.base).pipe(
  //       map((resp) => resp.data), // unwrap the document
  //       catchError((err) => {
  //         console.error('[UserSettingsService] GET failed', err);
  //         return of(undefined); // emit undefined instead of crashing
  //       }),
  //     );
  //   }

  // creates new user-settings record in CouchDB
  create(doc: UserSettingsDoc): Observable<ApiResponse<UserSettingsDoc>> {
    return this.http.post<ApiResponse<UserSettingsDoc>>(this.base, doc, this.authOptions());
  }

  // TO DO - check, if offline, show warning that changes will not be saved to server
  update(doc: UserSettingsDoc): Observable<ApiResponse<UserSettingsDoc>> {
    const payload: UserSettingsDoc = {
      displayName: doc.displayName,
      email: doc.email,
      syncMethod: doc.syncMethod,
      favoriteNoteIds: doc.favoriteNoteIds ?? [],
      autosaveEnabled: doc.autosaveEnabled,
      showFavorites: doc.showFavorites,
      showRecent: doc.showRecent,
      recentDays: doc.recentDays,
      savedSearches: Array.isArray(doc.savedSearches) ? doc.savedSearches : undefined,
    };
    console.log(' PUT user-settings: ', payload);

    this.accountSettings = {
      ...(this.accountSettings || {}),
      ...payload,
    };
    this.rebuildMergedSettings();

    // Write to IndexedDB, and then to CouchDB
    this.dataOfflineService
      .saveUserSettings(payload, true)
      .catch((err) => console.error('Failed to save user-settings to IndexedDB:', err));
    return this.http.put<ApiResponse<UserSettingsDoc>>
        (this.base, payload, this.authOptions()).pipe(
      tap((resp) => {
        if (resp.status === 'success' && resp.data) {
          this.accountSettings = {
            ...(this.accountSettings || {}),
            ...resp.data,
          };
          this.rebuildMergedSettings();
        }
      }),
    );
  }

  updateFavorites(favoriteNoteIds: string[]): Observable<ApiResponse<UserSettingsDoc>> {
    const payload: UserSettingsDoc = {
      favoriteNoteIds: Array.isArray(favoriteNoteIds) ? favoriteNoteIds : [],
    };
    this.dataOfflineService
      .saveUserSettings(payload, true)
      .catch((err) => console.error('Failed to save favorites to IndexedDB:', err));

    if (!navigator.onLine) {
      this.accountSettings = {
        ...(this.accountSettings || {}),
        favoriteNoteIds: payload.favoriteNoteIds,
      };
      this.rebuildMergedSettings();
      return of({ status: 'success', data: payload });
    }

    return this.http.put<ApiResponse<UserSettingsDoc>>(
      this.favoritesEndpoint,
      payload,
      this.authOptions(),
    ).pipe(
      tap((resp) => {
        if (resp.status === 'success' && resp.data) {
          this.accountSettings = {
            ...(this.accountSettings || {}),
            ...resp.data,
          };
          this.rebuildMergedSettings();
        }
      }),
    );
  }

  updateSavedSearches(savedSearches: any[]): Observable<ApiResponse<UserSettingsDoc>> {
    const payload: UserSettingsDoc = {
      savedSearches: Array.isArray(savedSearches) ? savedSearches : [],
    };

    this.accountSettings = {
      ...(this.accountSettings || {}),
      savedSearches: payload.savedSearches,
    };
    this.rebuildMergedSettings();

    this.dataOfflineService
      .saveUserSettings(payload, true)
      .catch((err) => console.error('Failed to save saved searches to IndexedDB:', err));

    if (!navigator.onLine) {
      this.accountSettings = {
        ...(this.accountSettings || {}),
        savedSearches: payload.savedSearches,
      };
      this.rebuildMergedSettings();
      return of({ status: 'success', data: payload });
    }

    return this.http.put<ApiResponse<UserSettingsDoc>>(
      this.base,
      payload,
      this.authOptions(),
    ).pipe(
      tap((resp) => {
        if (resp.status === 'success' && resp.data) {
          this.accountSettings = {
            ...(this.accountSettings || {}),
            ...resp.data,
          };
          this.rebuildMergedSettings();
        }
      }),
    );
  }

  // this function allows components to update only part of the user settings
  // example usage:   this.userSettings.setLocal({ primaryColor: '#ff6600' });
  setLocal(partial: Partial<UserSettingsDoc>): void {
    const { display, account } = this.splitSettings(partial);

    if (Object.keys(account).length) {
      this.accountSettings = {
        ...(this.accountSettings || {}),
        ...account,
      };
    }

    if (Object.keys(display).length) {
      this.deviceDisplaySettings = {
        ...this.deviceDisplaySettings,
        ...display,
      };
    }

    this.rebuildMergedSettings();

    // Optionally apply styles immediately (e.g., primaryColor)
    if (display.primaryColor) {
      const colorName = this.resolvePrimaryColorName(display.primaryColor);
      if (colorName) {
        this.layoutService.appState.update((state: any) => ({
          ...state,
          primary: colorName,
        }));
        this.applyPrimePresetForPrimary(colorName);
      }
      this.applyPrimaryColorValue(display.primaryColor);
    }
    if (display.editorZoomFactor !== undefined) {
      console.log('[UserSettings] setLocal: setting --fontScaleFactor to', display.editorZoomFactor + '%', 'from factor', display.editorZoomFactor);
      document.documentElement.style.setProperty('--fontScaleFactor',
        `${display.editorZoomFactor}%`);
      document.documentElement.style.setProperty('--fontScaleRatio', `${display.editorZoomFactor / 100}`);
      this.editorZoomFactorSubject.next(display.editorZoomFactor);
    }
    if (display.systemZoomFactor !== undefined) {
      applySystemZoom(display.systemZoomFactor);
      this.systemZoomFactorSubject.next(display.systemZoomFactor);
    }
    if (display.theme) {
      this.applyTheme(display.theme);
    }
    if (typeof display.darkMode === 'boolean') {
      this.layoutService.appState.update((state: any) => ({
        ...state,
        darkMode: display.darkMode,
      }));
      localStorage.setItem('darkMode', display.darkMode ? 'true' : 'false');
    }
  }

  // Zoom
  applyZoom(settings: UserSettingsDoc) {
    const factor = settings.systemZoomFactor ?? 100;
    applySystemZoom(factor);
    document.documentElement.style.setProperty('--editorZoomFactor', 
        String(factor / 100));
  }

  setSystemZoomFactor(systemZoomFactor: number): void {
    this.systemZoomFactorSubject.next(systemZoomFactor);
  }

  getSystemZoomFactor(): number {
    return this.systemZoomFactorSubject.value;
  }

  setEditorZoomFactor(editorZoomFactor: number): void {
    this.setLocal({ editorZoomFactor });
  }

  getEditorZoomFactor(): number {
    return this.editorZoomFactorSubject.value;
  }

  //####################################################################################
  //#### Theme Management
  //####################################################################################

  applyTheme(themeName: string) {
    this.clearExistingThemes();
    document.body.classList.add(`${this.themeClassPrefix}${themeName}`);
    localStorage.setItem('selectedTheme', themeName);
  }

  getAvailableThemes(): string[] {
    return Object.keys(this.themes);
  }

  loadSavedTheme() {
    const savedTheme = localStorage.getItem('selectedTheme');
    if (savedTheme && this.themes[savedTheme]) {
      this.applyTheme(savedTheme);
    }
  }

  private clearExistingThemes() {
    Object.keys(this.themes).forEach((theme) => {
      document.body.classList.remove(`${this.themeClassPrefix}${theme}`);
    });
  }

  private extractDeviceDisplaySettings(device: DeviceDoc): Partial<UserSettingsDoc> {
    const normalizedPrimary = this.normalizePrimaryColor(device.primaryColor);
    return {
      theme: device.theme,
      primaryColor: normalizedPrimary ?? device.primaryColor,
      darkMode: typeof device.darkMode === 'boolean' ? device.darkMode : undefined,
      systemZoomFactor: device.systemZoomFactor,
      editorZoomFactor: device.editorZoomFactor,
      homeView: device.homeView,
    };
  }

  private stripDisplaySettings(settings: UserSettingsDoc): UserSettingsDoc {
    const {
      theme,
      primaryColor,
      darkMode,
      systemZoomFactor,
      editorZoomFactor,
      homeView,
      ...rest
    } =
        settings || {};
    return rest;
  }

  private mergeDisplaySettings(
    userSettings: Partial<UserSettingsDoc>,
    deviceSettings: Partial<UserSettingsDoc>,
  ): DisplaySettings {
    return {
      theme: deviceSettings.theme ?? userSettings.theme,
      primaryColor: deviceSettings.primaryColor ?? userSettings.primaryColor,
      darkMode:
        typeof deviceSettings.darkMode === 'boolean'
          ? deviceSettings.darkMode
          : userSettings.darkMode,
      systemZoomFactor: deviceSettings.systemZoomFactor ?? userSettings.systemZoomFactor,
      editorZoomFactor: deviceSettings.editorZoomFactor ?? userSettings.editorZoomFactor,
      homeView: deviceSettings.homeView ?? userSettings.homeView,
    };
  }

  private splitSettings(partial: Partial<UserSettingsDoc>): {
    display: Partial<DisplaySettings>;
    account: Partial<UserSettingsDoc>;
  } {
    const display: Partial<DisplaySettings> = {};
    const account: Partial<UserSettingsDoc> = {};
    Object.entries(partial).forEach(([key, value]) => {
      if (value === undefined) {
        return;
      }
      if (
        key === 'theme' ||
        key === 'primaryColor' ||
        key === 'darkMode' ||
        key === 'systemZoomFactor' ||
        key === 'editorZoomFactor' ||
        key === 'homeView'
      ) {
        (display as Record<string, unknown>)[key] = value;
      } else {
        (account as Record<string, unknown>)[key] = value;
      }
    });
    return { display, account };
  }

  private rebuildMergedSettings(): void {
    this.userSettings = {
      ...(this.accountSettings || {}),
      ...(this.deviceDisplaySettings || {}),
    };
    this.userSettingsSubject.next(this.userSettings);
  }

  private applyPrimaryColorValue(value?: string | null) {
    const resolved = this.resolvePrimaryColorValue(value);
    if (!resolved) {
      return;
    }
    document.documentElement.style.setProperty('--p-primary-color', resolved);
    document.documentElement.style.setProperty('--primary-color', resolved);
    localStorage.setItem('primaryColor', resolved);
  }

  private resolvePrimaryColorName(value?: string | null): string | null {
    if (!value) {
      return null;
    }
    const trimmed = value.trim().toLowerCase();
    if (this.isHexColor(trimmed)) {
      return this.matchPrimaryNameFromHex(trimmed);
    }
    const primaryNames = [
      'emerald',
      'green',
      'lime',
      'orange',
      'amber',
      'yellow',
      'teal',
      'cyan',
      'sky',
      'blue',
      'indigo',
      'violet',
      'purple',
      'fuchsia',
      'pink',
      'rose',
    ];
    return primaryNames.includes(trimmed) ? trimmed : null;
  }

  private resolvePrimaryColorValue(value?: string | null): string | null {
    if (!value) {
      return null;
    }
    const trimmed = value.trim();
    if (this.isHexColor(trimmed)) {
      return trimmed.startsWith('#') ? trimmed : `#${trimmed}`;
    }

    const presetName = (this.layoutService.appState().preset || 'Aura') as 
        keyof typeof presets;
    const preset = presets[presetName] ?? Aura;
    const palette = (preset as any).primitive?.[trimmed];
    const hex = palette?.['500'] ?? palette?.[500];
    return typeof hex === 'string' ? hex : null;
  }

  private isHexColor(value: string): boolean {
    return /^#?[0-9a-fA-F]{6}$/.test(value);
  }

  private normalizePrimaryColor(value?: string | null): string | null {
    if (!value) {
      return null;
    }
    const trimmed = value.trim();
    if (this.isHexColor(trimmed)) {
      return this.matchPrimaryNameFromHex(trimmed);
    }
    return trimmed;
  }

  private matchPrimaryNameFromHex(value: string): string | null {
    const normalized = value.replace('#', '').toLowerCase();
    const presetName = (this.layoutService.appState().preset || 'Aura') as 
        keyof typeof presets;
    const preset = presets[presetName] ?? Aura;
    const primitive = (preset as any).primitive ?? {};

    for (const [name, palette] of Object.entries(primitive)) {
      if (!palette || typeof palette !== 'object') {
        continue;
      }
      const shade = (palette as any)['500'] ?? (palette as any)[500];
      if (typeof shade === 'string' && shade.replace('#', '').toLowerCase() === 
            normalized) {
        return name;
      }
    }

    return null;
  }

  private normalizeFavoriteNoteIds(value: unknown): string[] {
    if (!Array.isArray(value)) {
      return [];
    }
    return value.filter((id) => typeof id === 'string');
  }

  private applyPrimePresetForPrimary(colorName: string) {
    const presetName = (this.layoutService.appState().preset || 'Aura') as 
        keyof typeof presets;
    const preset = presets[presetName] ?? Aura;
    const palette = (preset as any).primitive?.[colorName];
    if (!palette) {
      return;
    }
    updatePreset({
      semantic: {
        primary: palette,
        colorScheme: {
          light: {
            primary: {
              color: '{primary.500}',
              contrastColor: '#ffffff',
              hoverColor: '{primary.600}',
              activeColor: '{primary.700}',
            },
            highlight: {
              background: '{primary.50}',
              focusBackground: '{primary.100}',
              color: '{primary.700}',
              focusColor: '{primary.800}',
            },
          },
          dark: {
            primary: {
              color: '{primary.400}',
              contrastColor: '{surface.900}',
              hoverColor: '{primary.300}',
              activeColor: '{primary.200}',
            },
            highlight: {
              background: 'color-mix(in srgb, {primary.400}, transparent 84%)',
              focusBackground: 'color-mix(in srgb, {primary.400}, transparent 76%)',
              color: 'rgba(255,255,255,.87)',
              focusColor: 'rgba(255,255,255,.87)',
            },
          },
        },
      },
    });
  }

  private themeClassPrefix = 'theme-';
  private themes: {
    [key: string]: {
      backgroundColor: string;
      textColor: string;
      textColorFaded: string;
      buttonColor: string;
      buttonTextColor: string;
      buttonBorderColor: string;
      headerBackgroundColor: string;
      editFieldBackgroundColor: string;
      viewFieldBackgroundColor: string;
      toolbarBackgroundColor: string;
      deleteButtonColor: string;
      closeButtonColor: string;
      spinnerColor: string;
      borderColor: string;
      tabsColor: string;
      editPageBackgroundColor: string;
      editPageHeaderBackgroundColor: string;
      project1Color: string;
      project2Color: string;
      project3Color: string;
      project4Color: string;
      project5Color: string;
      project6Color: string;
      project7Color: string;
      project8Color: string;
      project9Color: string;
      project10Color: string;
      project11Color: string;
      project12Color: string;
    };
  } = {
    light: {
      backgroundColor: '#dddddd',
      textColor: '#000000',
      textColorFaded: '#444444',
      buttonColor: '#cccccc',
      buttonTextColor: '#ffffff',
      buttonBorderColor: '#007bff',
      headerBackgroundColor: 'eeeeee',
      editFieldBackgroundColor: '#ffffff',
      viewFieldBackgroundColor: '#f9f9f9',
      toolbarBackgroundColor: '#f9f9f9',
      deleteButtonColor: '#ff0000',
      closeButtonColor: '#6c757d',
      spinnerColor: '#ff0000',
      borderColor: '#000000',
      tabsColor: '#007bff',
      editPageBackgroundColor: '#ff0000',
      editPageHeaderBackgroundColor: 'red',
      project1Color: '#ff0000',
      project2Color: '#ff8000',
      project3Color: '#ffbf00',
      project4Color: '#ffff00',
      project5Color: '#80ff00',
      project6Color: '#00ff00',
      project7Color: '#00ff80',
      project8Color: '#00ffbf',
      project9Color: '#00ffff',
      project10Color: '#0080ff',
      project11Color: '#0000ff',
      project12Color: '#8000ff',
    },
    dark: {
      backgroundColor: '#333333',
      textColor: '#dddddd',
      textColorFaded: '#444444',
      buttonColor: '#007bff',
      buttonTextColor: '#ffffff',
      buttonBorderColor: '#007bff',
      headerBackgroundColor: 'eeeeee',
      editFieldBackgroundColor: '#ffffff',
      viewFieldBackgroundColor: '#f9f9f9',
      toolbarBackgroundColor: '#f9f9f9',
      deleteButtonColor: '#dc3545',
      closeButtonColor: '#6c757d',
      spinnerColor: '#007bff',
      borderColor: '#222222',
      tabsColor: '#007bff',
      editPageBackgroundColor: '#ff0000',
      editPageHeaderBackgroundColor: 'red',
      project1Color: '#ff0000',
      project2Color: '#ff8000',
      project3Color: '#ffbf00',
      project4Color: '#ffff00',
      project5Color: '#80ff00',
      project6Color: '#00ff00',
      project7Color: '#00ff80',
      project8Color: '#00ffbf',
      project9Color: '#00ffff',
      project10Color: '#0080ff',
      project11Color: '#0000ff',
      project12Color: '#8000ff',
    },
  };
}
